import { motion } from 'framer-motion';
import { Video, Mic, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import type { CommunicationMode } from '../types/app';
import { preferencesCopy } from '../config/copy';

interface PreferencesScreenProps {
  onSelect: (mode: CommunicationMode) => void;
  availableModes?: CommunicationMode[];
  /**
   * W5: the mode the advisor is already in, when Portal knows one. Shown as a
   * line of context and nothing more — it does not preselect, disable or reorder
   * any card. Rule 1 makes the SELLER adapt to the customer, not the reverse.
   */
  sellerMode?: CommunicationMode | null;
}

export function PreferencesScreen({
  onSelect,
  availableModes,
  sellerMode,
}: PreferencesScreenProps) {
  const [selected, setSelected] = useState<CommunicationMode | null>(null);

  const preferences = [
    {
      id: 'video' as const,
      icon: Video,
      title: preferencesCopy.cards.video.title,
      description: preferencesCopy.cards.video.description,
    },
    {
      id: 'audio' as const,
      icon: Mic,
      title: preferencesCopy.cards.audio.title,
      description: preferencesCopy.cards.audio.description,
    },
    {
      id: 'chat' as const,
      icon: MessageSquare,
      title: preferencesCopy.cards.chat.title,
      description: preferencesCopy.cards.chat.description,
    },
  ];

  const handleSelect = (id: CommunicationMode) => {
    setSelected(id);
  };

  const handleContinue = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeInOut' }}
      className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0] px-6 py-12"
    >
      <div className="max-w-4xl w-full flex flex-col items-center space-y-16">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-center space-y-4"
        >
          <h1
            className="text-4xl font-serif font-light text-[#1A1A2E] tracking-wide"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {preferencesCopy.heading}
          </h1>
          <div className="w-16 h-px bg-[#C9A961] mx-auto" />
          <p
            className="text-sm text-[#1A1A2E]/60 tracking-wide font-sans pt-2"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {preferencesCopy.subHeading}
          </p>
          {sellerMode && (
            <p
              className="text-sm text-[#C9A961] tracking-wide font-sans"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {preferencesCopy.advisorPresence[sellerMode]}
            </p>
          )}
        </motion.div>

        {/* Preference Cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full"
        >
          {preferences.map((pref, index) => {
            const Icon = pref.icon;
            const isSelected = selected === pref.id;
            const isDisabled =
              availableModes && !availableModes.includes(pref.id);

            return (
              <motion.button
                key={pref.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.15, duration: 0.8 }}
                onClick={() => !isDisabled && handleSelect(pref.id)}
                disabled={isDisabled}
                className={`group relative p-10 bg-white transition-all duration-500 text-left ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'shadow-xl border-2 border-[#C9A961]'
                    : 'shadow-md border-2 border-transparent hover:border-[#C9A961]/30 hover:shadow-lg'
                }`}
              >
                <div className="space-y-8">
                  <Icon
                    className={`w-10 h-10 transition-colors duration-500 ${
                      isDisabled
                        ? 'text-[#1A1A2E]/20'
                        : isSelected
                        ? 'text-[#C9A961]'
                        : 'text-[#1A1A2E]/30 group-hover:text-[#C9A961]/60'
                    }`}
                    strokeWidth={1.5}
                  />
                  <div className="space-y-3">
                    <h3
                      className="text-lg font-serif text-[#1A1A2E] leading-snug"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {pref.title}
                    </h3>
                    <p
                      className="text-sm text-[#1A1A2E]/60 leading-relaxed font-sans"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {pref.description}
                    </p>
                  </div>
                </div>

                {isSelected && (
                  <motion.div
                    layoutId="selected-indicator"
                    className="absolute top-6 right-6 w-8 h-8 rounded-full bg-[#C9A961] flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <svg
                      width="14"
                      height="12"
                      viewBox="0 0 14 12"
                      fill="none"
                      className="text-white"
                    >
                      <path
                        d="M1 6L5 10L13 2"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Helper Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="text-sm text-[#1A1A2E]/60 text-center max-w-md font-sans italic"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          {preferencesCopy.helperText}
        </motion.p>

        {/* Continue Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          onClick={handleContinue}
          disabled={!selected}
          className={`group relative px-14 py-5 text-xs tracking-[0.2em] uppercase overflow-hidden transition-all duration-500 border-2 ${
            selected
              ? 'bg-[#1A1A2E] text-[#F8F6F0] hover:bg-[#C9A961] border-[#1A1A2E] hover:border-[#C9A961]'
              : 'bg-white text-[#1A1A2E]/30 border-[#1A1A2E]/10 cursor-not-allowed'
          }`}
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <span className="relative z-10">{preferencesCopy.cta}</span>
        </motion.button>
      </div>
    </motion.div>
  );
}
